import { useIntervalFn, type UseIntervalFnOptions } from '@vueuse/core';
import { toValue, watch, type WatchSource } from 'vue';

type UseIntervalFnParams = Parameters<typeof useIntervalFn>;

/**
 * @param fn The callback function to invoke periodically if `condition` is true.
 * @param config.condition (Watched reactively) Starts or stops the _timer_ for invoking `fn`
 * @param config.immediate Flag to start the _timer_ of the interval
 * @param config.immediateCallback Flag for invoking `fn` immedately when `resume()` is called.
 * @param config.interval Length in ms for the duration of the timer.
 */
export const useConditionalInterval = (
    fn: UseIntervalFnParams[0],
    {
        interval,
        condition = () => true,
        immediate = toValue(condition),
        immediateCallback,
    }: {
        interval?: UseIntervalFnParams[1];
        condition?: WatchSource<boolean>;
    } & UseIntervalFnOptions,
): ReturnType<typeof useIntervalFn> => {
    const { pause, resume, isActive } = useIntervalFn(fn, interval, { immediate, immediateCallback });

    watch(condition, shouldRun => {
        shouldRun ? resume() : pause();
    });

    return {
        pause,
        resume,
        isActive,
    };
};
